import { FC, useState } from 'react';
import { Box, withStyles, CircleButton, CportalServiceItem } from 'vopay';
import { useTranslation } from 'react-i18next';
import { Title, Toggle } from 'UI';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useDialog } from '@/providers/Dialog.provider';
import { useToast } from '@/providers/Toast.provider';
import { useDataFetchIndicator } from '@/hooks';
import CredentialsDialog from './CredentialsDialog';
import Services from './Services';
import useCportal from '@/hooks/useApi/useCportal';
import styles from './CportalTab.module.scss';

const cn = withStyles(styles);

type CportalTabProps = {
  isCportalEnabled: boolean;
  clientName: string;
  clientEmail: string;
  clientPhone: string;
  clientID: string;
  cPortalLink: string;
  services: CportalServiceItem[];
  onUpdate: () => void;
};

const CportalTab: FC<CportalTabProps> = ({
  isCportalEnabled,
  clientName,
  clientEmail,
  clientPhone,
  clientID,
  cPortalLink,
  services,
  onUpdate,
}) => {
  const dialog = useDialog();
  const alertDialog = useAlertDialog();
  const toast = useToast();
  const { sendCportalEmail } = useCportal();
  const { t } = useTranslation('translations', { keyPrefix: 'Customers.CportalTab' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const [shouldEnableCportal, setShouldEnableCportal] = useState(isCportalEnabled);
  const [, setDataFetchingInfo] = useDataFetchIndicator();

  async function handleCredentialsDialog() {
    setShouldEnableCportal(true);
    let wasEnabled = false;
    await dialog.open(({ onCancel }) => (
      <CredentialsDialog
        clientName={clientName}
        clientEmail={clientEmail}
        clientPhone={clientPhone}
        clientID={clientID}
        onUpdate={() => {
          wasEnabled = true;
          onUpdate();
        }}
        onClose={onCancel}
      />
    ));
    if (!wasEnabled) setShouldEnableCportal(false);
  }

  async function handleResendDialog() {
    const result = await alertDialog.caution(
      t('resendInviteTitle', { defaultValue: 'Resend Invite' }),
      t('resendInviteDescription', {
        defaultValue: 'Are you sure you would like to resend invitation to this client account?',
      }),
      {
        abortLabel: tGlobal('Buttons.back', { defaultValue: 'Back' }),
        confirmLabel: tGlobal('Buttons.confirm', { defaultValue: 'Confirm' }),
      },
    );

    if (result === 'confirm') {
      try {
        setDataFetchingInfo(true);
        await sendCportalEmail({
          ClientAccountID: clientID,
          EmailType: 'WelcomeEmail',
        });
        toast.success(
          t('sendCportalEmailSuccess', {
            defaultValue: 'cPortal Invitation Sent Successfully',
          }),
        );
      } catch (err) {
        toast.neutral(
          t('sendCportalEmailError', {
            defaultValue: 'Failed to send the cPortal welcome email',
          }),
        );
      } finally {
        setDataFetchingInfo(false);
      }
    }
  }

  return (
    <>
      <div className={cn('titleContainer')}>
        <Toggle
          name="enable-cportal-check"
          checked={shouldEnableCportal}
          readOnly={isCportalEnabled}
          onChange={async (e) => {
            if (e.target.checked) {
              await handleCredentialsDialog();
            }
          }}
          label={<Title level="5">{t('title', { defaultValue: 'cPortal' })}</Title>}
        />
      </div>

      {isCportalEnabled && (
        <div className={cn('cportalLink')}>
          <span>{t('cportalLink', { defaultValue: 'cPortal Link' })}</span>
          <Box gapSize="12px" ai="center">
            <span className={cn('link')}>{cPortalLink}</span>
            <CircleButton
              icon="content_copy"
              circleFill={false}
              colorScheme="action"
              onClick={() => {
                navigator.clipboard.writeText(cPortalLink.toString());
              }}
            />
            <CircleButton icon="send" circleFill={false} colorScheme="action" iconFill onClick={handleResendDialog} />
          </Box>
        </div>
      )}

      <Services isEnabled={shouldEnableCportal} services={services} />
    </>
  );
};

export default CportalTab;
